import React, { useState, useEffect } from 'react';
import { aiApi, acquistiApi } from '../../lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { Loader2, Sparkles } from 'lucide-react';
import CategoriaSelect, { NESSUNA } from './CategoriaSelect';
import { fmtSoldi } from '../../lib/acquisti';

const COLORE_CONFIDENZA = {
  alta: 'bg-emerald-100 text-emerald-800',
  media: 'bg-amber-100 text-amber-800',
  bassa: 'bg-red-100 text-red-800',
};

/** Proposte AI di categoria per i prodotti da classificare: l'utente conferma. */
export const ProposteAiDialog = ({ aperto, onChiudi, categorie, onApplicato }) => {
  const [caricando, setCaricando] = useState(false);
  const [dati, setDati] = useState(null);
  const [scelte, setScelte] = useState({});
  const [inclusi, setInclusi] = useState({});
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!aperto) return;
    setCaricando(true);
    setDati(null);
    aiApi.proponiCategorie(40)
      .then((res) => {
        setDati(res);
        const s = {}, i = {};
        (res.proposte || []).forEach((p) => {
          if (p.categoria_proposta) {
            s[p.prodotto_id] = p.categoria_proposta;
            i[p.prodotto_id] = p.confidenza !== 'bassa';
          }
        });
        setScelte(s);
        setInclusi(i);
      })
      .catch((e) => {
        toast.error(e.response?.data?.detail || 'Proposte non disponibili');
        onChiudi();
      })
      .finally(() => setCaricando(false));
  }, [aperto, onChiudi]);

  const selezionati = Object.keys(inclusi).filter((id) => inclusi[id] && scelte[id]);

  const applica = async () => {
    const gruppi = {};
    selezionati.forEach((id) => {
      gruppi[scelte[id]] = [...(gruppi[scelte[id]] || []), id];
    });
    setSalvando(true);
    try {
      let totale = 0;
      for (const [categoria, ids] of Object.entries(gruppi)) {
        const res = await acquistiApi.setCategoriaProdottiMultipli(ids, categoria);
        totale += res.prodotti_aggiornati || 0;
      }
      toast.success(`${totale} prodotti classificati e ricordati per i prossimi import`);
      onApplicato();
      onChiudi();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Applicazione non riuscita');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={aperto} onOpenChange={(v) => !v && onChiudi()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="proposte-ai-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Sparkles className="w-4 h-4 text-primary" /> Proposte AI di categoria
          </DialogTitle>
          <DialogDescription className="text-xs">
            L’AI propone, decidi tu: spunta le righe da applicare e correggi dove serve.
            Le scelte diventano regole per i prossimi import.
          </DialogDescription>
        </DialogHeader>

        {caricando && (
          <p className="text-sm text-muted-foreground flex items-center gap-2 py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Sto analizzando i prodotti da classificare…
          </p>
        )}

        {!caricando && dati && (
          <div className="space-y-3">
            <p className="text-[11px] text-muted-foreground">
              {dati.n_prodotti} prodotti analizzati · {dati.n_proposte} proposte · modello {dati.modello}
            </p>
            {(dati.proposte || []).map((p) => (
              <div
                key={p.prodotto_id}
                className="flex items-start gap-2 pb-2 border-b last:border-0"
                data-testid={`proposta-${p.prodotto_id}`}
              >
                <Checkbox
                  checked={!!inclusi[p.prodotto_id]}
                  onCheckedChange={(v) => setInclusi((s) => ({ ...s, [p.prodotto_id]: !!v }))}
                  className="mt-1 shrink-0"
                  data-testid={`proposta-check-${p.prodotto_id}`}
                />
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="text-sm font-medium leading-snug">{p.descrizione}</p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {p.fornitore_nome} · {fmtSoldi(p.spesa_totale)}
                  </p>
                  <div className="flex items-center gap-2">
                    <CategoriaSelect
                      valore={scelte[p.prodotto_id]}
                      categorie={categorie}
                      placeholder="Scegli categoria"
                      onChange={(v) => setScelte((s) => ({
                        ...s, [p.prodotto_id]: v === NESSUNA ? null : v,
                      }))}
                      testid={`proposta-categoria-${p.prodotto_id}`}
                      className="h-8 flex-1"
                    />
                    {p.confidenza && (
                      <Badge className={`text-[10px] shrink-0 ${COLORE_CONFIDENZA[p.confidenza] || ''}`}>
                        {p.confidenza}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {(dati.proposte || []).length === 0 && (
              <p className="text-sm text-muted-foreground">Nessun prodotto da classificare.</p>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onChiudi} data-testid="proposte-ai-annulla">Annulla</Button>
          <Button
            onClick={applica}
            disabled={!selezionati.length || salvando}
            data-testid="proposte-ai-applica"
          >
            {salvando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Applica {selezionati.length || ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProposteAiDialog;
